import { useState, useCallback } from 'react';
import { comedorLectoresOfflineApi } from '../api/comedorLectoresOfflineApi';
import { showErrorToast, showSuccessToast } from '../utils/errorHandler';

const unwrap = (response) => {
  const body = response?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (body?.data && typeof body.data === 'object') return body.data;
  return body;
};

export const useComedorLectoresOffline = () => {
  const [estaciones, setEstaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');
    if (!selectedProject?.id) {
      setEstaciones([]);
      return [];
    }
    const response = await comedorLectoresOfflineApi.getAll({
      id_proyecto: selectedProject.id,
    });
    const data = unwrap(response) || [];
    setEstaciones(Array.isArray(data) ? data : []);
    return Array.isArray(data) ? data : [];
  }, []);

  const fetchByProyecto = useCallback(
    async (proyectoId) => {
      setLoading(true);
      setError(null);
      try {
        const response = await comedorLectoresOfflineApi.getAll({
          id_proyecto: proyectoId,
        });
        const data = unwrap(response) || [];
        const list = Array.isArray(data) ? data : [];
        setEstaciones(list);
        return list;
      } catch (err) {
        const msg = showErrorToast(err, 'Error al cargar estaciones offline');
        setError(msg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createEstacion = useCallback(
    async (payload) => {
      setLoading(true);
      setError(null);
      try {
        const response = await comedorLectoresOfflineApi.create(payload);
        showSuccessToast('Estación offline creada');
        await reload();
        return unwrap(response);
      } catch (err) {
        const msg = showErrorToast(err, 'Error al crear estación offline');
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [reload],
  );

  const updateEstacion = useCallback(
    async (id, payload) => {
      setLoading(true);
      setError(null);
      try {
        const response = await comedorLectoresOfflineApi.update(id, payload);
        showSuccessToast('Estación offline actualizada');
        await reload();
        return unwrap(response);
      } catch (err) {
        const msg = showErrorToast(err, 'Error al actualizar estación offline');
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [reload],
  );

  const deactivateEstacion = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        await comedorLectoresOfflineApi.update(id, { activo: false });
        showSuccessToast('Estación offline desactivada');
        await reload();
      } catch (err) {
        const msg = showErrorToast(err, 'Error al desactivar estación offline');
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [reload],
  );

  const regenerarToken = useCallback(
    async (id) => {
      setLoading(true);
      setError(null);
      try {
        const response = await comedorLectoresOfflineApi.regenerarToken(id);
        showSuccessToast('Token regenerado. Actualice Electron con el nuevo valor.');
        await reload();
        return unwrap(response);
      } catch (err) {
        const msg = showErrorToast(err, 'Error al regenerar token');
        setError(msg);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [reload],
  );

  const clearError = useCallback(() => setError(null), []);

  return {
    estaciones,
    loading,
    error,
    fetchByProyecto,
    createEstacion,
    updateEstacion,
    deactivateEstacion,
    regenerarToken,
    clearError,
  };
};

export default useComedorLectoresOffline;
